import StyledWrapper from "@insite/client-framework/Common/StyledWrapper";
import WidgetModule from "@insite/client-framework/Types/WidgetModule";
import WidgetProps from "@insite/client-framework/Types/WidgetProps";
import { BaseTheme } from "@insite/mobius/globals/baseTheme";
import Link from "@insite/mobius/Link";
import Typography from "@insite/mobius/Typography";
import { breakpointMediaQueries } from "@insite/mobius/utilities";
import getColor from "@insite/mobius/utilities/getColor";
import InjectableCss from "@insite/mobius/utilities/InjectableCss";
import CENDownloadIcon from "@tCentro/Icons/CENDownloadIcon";
import { TYPOGRAPHY } from "@tCentro/theme/design";
import React from "react";
import { css } from "styled-components";

interface Fields {
    heading: string;
    description: string;
    buttonLabel: string;
    buttonUrl: string;
    image: string;
    checkboxFieldValue: boolean;
}
type Props = WidgetProps & { fields: Fields };

export interface LineCardCtaStyles {
    outer?: InjectableCss;
    inner?: InjectableCss;
    image?: InjectableCss;
    textColumn?: InjectableCss;
    heading?: InjectableCss;
    description?: InjectableCss;
    button?: InjectableCss;
    downloadBtnWrapper?: InjectableCss;
}

export const lineCardCtaStyles: LineCardCtaStyles = {
    downloadBtnWrapper: {
        css: css`
            flex: 1;
            justify-content: flex-end;
            display: inline-flex;
            margin-top: 20px;

            ${({ theme }: { theme: BaseTheme }) =>
                breakpointMediaQueries(theme, [
                    // xs: button row spans the full card width
                    css`
                        flex: none;
                        width: 100%;
                        justify-content: center;
                        display: flex;
                        margin-top: 8px;
                    `,
                    // sm / md (tablet): button keeps its own width, text column gets the rest
                    css`
                        flex: 0 0 auto;
                        justify-content: flex-end;
                        display: inline-flex;
                        margin-top: 0;
                    `,
                    css`
                        flex: 0 0 auto;
                        justify-content: flex-end;
                        display: inline-flex;
                        margin-top: 0;
                    `,
                    null,
                    null,
                ])}
        `,
    },
    outer: {
        css: css`
            background: ${getColor("primary")};
            color: ${getColor("primary.contrast")};
            margin-top: 104px;
             margin-left: 20px;
            margin-right: 20px;
            ${({ theme }: { theme: BaseTheme }) =>
                breakpointMediaQueries(theme, [
                    css`
                        margin-top: 40px;
                    `,
                    css`
                        margin-top: 64px;
                    `,
                    null,
                    null,
                    null,
                ])}
        `,
    },
    inner: {
        css: css`
            display: flex;
            align-items: center;
            gap: 24px;
            padding-left: 32px;
            ${({ theme }: { theme: BaseTheme }) =>
                breakpointMediaQueries(theme, [
                    // xs: image on top, then left-aligned text, then full-width button
                    css`
                        flex-direction: column;
                        align-items: stretch;
                        text-align: left;
                        gap: 16px;
                        padding: 24px 16px;
                    `,
                    css`
                        gap: 16px;
                        padding-left: 24px;
                        padding-right: 24px;
                    `,
                    css`
                        gap: 16px;
                        padding-left: 24px;
                        padding-right: 24px;
                    `,
                    null,
                    null,
                ])}
        `,
    },
    image: {
        css: css`
            width: 195px;
            height: 195px;
            flex: 0 0 auto;
            margin-top: -17px;
            margin-bottom: -17px;
            ${({ theme }: { theme: BaseTheme }) =>
                breakpointMediaQueries(theme, [
                    css`
                        width: 180px;
                        height: 180px;
                        align-self: center;
                        margin: 0 auto;
                        object-fit: contain;
                    `,
                    css`
                        width: 160px;
                        height: 160px;
                        margin-top: -12px;
                        margin-bottom: -12px;
                    `,
                    css`
                        width: 160px;
                        height: 160px;
                        margin-top: -12px;
                        margin-bottom: -12px;
                    `,
                    null,
                    null,
                ])}
        `,
    },
    textColumn: {
        css: css`
            flex: 1 1 auto;
            ${({ theme }: { theme: BaseTheme }) =>
                breakpointMediaQueries(theme, [
                    null,
                    css`
                        flex: 1 1 0;
                        min-width: 0;
                        padding: 24px 0;
                    `,
                    css`
                        flex: 1 1 0;
                        min-width: 0;
                        padding: 24px 0;
                    `,
                    null,
                    null,
                ])}
        `,
    },
    heading: {
        css: css`
            font-size: 20px;
            font-weight: ${TYPOGRAPHY.weight.mediumbold};
            color: inherit;
            margin-bottom: 4px;
            ${({ theme }: { theme: BaseTheme }) =>
                breakpointMediaQueries(theme, [
                    css`
                        line-height: 28px;
                        margin-bottom: 8px;
                    `,
                    null,
                    null,
                    null,
                    null,
                ])}
        `,
    },
    description: {
        css: css`
            font-size: 13px;
            line-height: 20px;
            color: inherit;
            ${({ theme }: { theme: BaseTheme }) =>
                breakpointMediaQueries(theme, [
                    css`
                        font-size: 14px;
                        line-height: 22px;
                    `,
                    null,
                    null,
                    null,
                    null,
                ])}
        `,
    },
    button: {
        css: css`
            flex: 0 0 auto;
            background: ${getColor("secondary")} !important;
            color: ${getColor("secondary.contrast")} !important;
            border-radius: 24px !important;
            padding: 10px 24px !important;
            font-weight: 600;
            &:focus-visible {
                outline: 2px solid #fff;
                outline-offset: 2px;
            }
        `,
    },
};

const LineCardCta = ({ fields }: Props) => {
    const styles = lineCardCtaStyles;
    const btnDownload = {
        css: css`
            padding: 12px 24px;
            width: 217px;
            height: 48px;
            background: white;
            border-radius: 24px;
            font-weight: 600;
            font-size: 14px;
            line-height: 16px;
            color: #8f0446;
            margin-right: 72px;
            span {
                color: #8f0446 !important;
                display: flex;
                align-items: center;
                gap: 8px;
            }
            ${({ theme }: { theme: BaseTheme }) =>
                breakpointMediaQueries(theme, [
                    // xs: button fills the card width
                    css`
                        margin-top: 15px;
                        width: 100%;
                        justify-content: center;
                        margin-right: 0px;
                    `,
                    css`
                        margin-top: 0;
                        width: 190px;
                    `,
                    css`
                        margin-top: 0;
                        width: 190px;
                    `,
                    null,
                    null,
                ])}
        `,
    };
    return (
        <StyledWrapper {...styles.outer}>
            <StyledWrapper {...styles.inner}>
                {fields.image && <StyledWrapper as="img" {...styles.image} src={fields.image} alt="" />}
                <StyledWrapper {...styles.textColumn}>
                    <Typography as="h2" {...styles.heading}>
                        {fields.heading}
                    </Typography>
                    <Typography as="p" {...styles.description}>
                        {fields.description}
                    </Typography>
                </StyledWrapper>
                {/* {fields.buttonUrl && (
                    <Link href={fields.buttonUrl} target="_blank" rel="noopener" {...styles.button}>
                        {fields.buttonLabel}
                    </Link>
                )} */}
                {fields.buttonUrl && (
                    <StyledWrapper {...styles.downloadBtnWrapper}>
                        <Link
                            href={fields.buttonUrl}
                            {...btnDownload}
                            target={fields.checkboxFieldValue ? "_blank" : "_self"}
                        >
                            <CENDownloadIcon></CENDownloadIcon> {fields.buttonLabel}
                        </Link>
                    </StyledWrapper>
                )}
            </StyledWrapper>
        </StyledWrapper>
    );
};

const widgetModule: WidgetModule = {
    component: LineCardCta,
    definition: {
        group: "Common",
        displayName: "CEN Line Card CTA",
        fieldDefinitions: [
            {
                name: "heading",
                displayName: "Heading",
                editorTemplate: "TextField",
                defaultValue: "Get the Latest Line Card",
                fieldType: "Translatable",
            },
            {
                name: "description",
                displayName: "Description",
                editorTemplate: "TextField",
                defaultValue: "",
                fieldType: "Translatable",
            },
            {
                name: "buttonLabel",
                displayName: "Button label",
                editorTemplate: "TextField",
                defaultValue: "Download Now",
                fieldType: "Translatable",
            },
            {
                name: "buttonUrl",
                displayName: "Button URL (PDF)",
                editorTemplate: "TextField",
                defaultValue: "",
                fieldType: "General",
            },
             {
                name: "checkboxFieldValue",
                displayName: "Open in a new tab",
                editorTemplate: "CheckboxField",
                defaultValue: false,
                fieldType: "General",
            },
            {
                name: "image",
                displayName: "Image",
                editorTemplate: "ImagePickerField",
                defaultValue: "",
                fieldType: "General",
            },
        ],
    },
};

export default widgetModule;